'use client';

import Link from '@/components/NoPrefetchLink';
import { useEffect, useMemo, useState } from 'react';
import type { AwardMaster, AwardsByCategory } from '@/lib/types';
import { withLocalePrefix } from '@/lib/localePath';
import type { Locale } from '@/lib/localePath';
import PageEmptyState from '@/components/PageEmptyState';
import { ScrollToTopButton } from './ScrollToTopButton';
import '@/styles/InfoPage.css';

type Props = { locale: Locale; initialCategories?: AwardsByCategory[] };

const awardSlug = (awardId: string): string => awardId.replace(/ /g, '-');

const CATEGORY_NAMES: Record<string, [string, string, string]> = {
    'MVP': ['MVP', 'MVP', 'MVP'],
    'Cy Young': ['サイ・ヤング賞', 'Cy Young Award', 'Premio Cy Young'],
    '新人王': ['新人王', 'Rookie of the Year', 'Novato del Año'],
    'Gold Glove': ['ゴールドグラブ賞', 'Gold Glove', 'Guante de Oro'],
    'Silver Slugger': ['シルバースラッガー賞', 'Silver Slugger', 'Bate de Plata'],
    'リリーバー': ['最優秀救援投手賞', 'Reliever Awards', 'Premios al Relevista'],
    '月間・週間賞': ['月間・週間賞', 'Monthly/Weekly Awards', 'Premios Mensuales/Semanales'],
    'その他個人賞': ['その他個人賞', 'Other Individual Awards', 'Otros Premios Individuales'],
    'コミュニティ賞': ['コミュニティ賞', 'Community Awards', 'Premios Comunitarios'],
    '軍人・愛国賞': ['軍人・愛国賞', 'Military/Patriot Awards', 'Premios Militares/Patrióticos'],
    'オールスター選出': ['オールスター選出', 'All-Star Selections', 'Selecciones All-Star'],
    'MLB Players Choice': ['MLB Players Choice', 'MLB Players Choice', 'MLB Players Choice'],
    'TSN': ['TSN（The Sporting News）', 'TSN (The Sporting News)', 'TSN (The Sporting News)'],
    'This Year in Baseball': ['This Year in Baseball', 'This Year in Baseball', 'This Year in Baseball'],
    'その他': ['その他', 'Other', 'Otros'],
};
const CATEGORY_ORDER = [
    'MVP',
    'Cy Young',
    '新人王',
    'Gold Glove',
    'Silver Slugger',
    'リリーバー',
    '月間・週間賞',
    'その他個人賞',
    'コミュニティ賞',
    '軍人・愛国賞',
    'オールスター選出',
    'MLB Players Choice',
    'TSN',
    'This Year in Baseball',
    'その他',
];

export default function AwardsClient({ locale, initialCategories }: Props) {
    const [categories, setCategories] = useState<AwardsByCategory[]>(initialCategories ?? []);
    const [loading, setLoading] = useState(false);
    const [activeCategory, setActiveCategory] = useState<string>('');

    const t = (ja: string, en: string, es: string) =>
        locale === 'en' ? en : locale === 'es' ? es : ja;

    const awardName = (award: AwardMaster) =>
        locale === 'en'
            ? (award.awardNameEn || award.awardId)
            : locale === 'es'
            ? (award.awardNameEs || award.awardNameEn || award.awardId)
            : (award.awardNameJa || award.awardId);

    const categoryName = (cat: string) => {
        const entry = CATEGORY_NAMES[cat];
        return entry ? t(entry[0], entry[1], entry[2]) : cat;
    };

    const orderedCategories = useMemo(() => {
        const orderIndex = new Map<string, number>(CATEGORY_ORDER.map((category, index) => [category, index]));
        return [...categories]
            .filter((cat) => cat.awards.length > 0)
            .sort((a, b) => {
                const indexA = orderIndex.get(a.category);
                const indexB = orderIndex.get(b.category);
                if (indexA !== undefined && indexB !== undefined) return indexA - indexB;
                if (indexA !== undefined) return -1;
                if (indexB !== undefined) return 1;
                return a.category.localeCompare(b.category, 'ja');
            });
    }, [categories]);
    const emptyStateLinks = [
        {
            href: withLocalePrefix(locale, '/mlb'),
            label: t('メジャーリーグ（MLB）トップ', 'MLB home', 'Inicio MLB'),
            note: t('MLBトップへ。', 'Open the MLB home page.', 'Abre la página principal de MLB.'),
        },
        {
            href: withLocalePrefix(locale, '/npb-awards'),
            label: t('プロ野球（NPB）アワード', 'NPB awards', 'Premios de NPB'),
            note: t('NPBアワード一覧へ。', 'Open the NPB awards list.', 'Abre la lista de premios de NPB.'),
        },
        {
            href: withLocalePrefix(locale, '/'),
            label: t('ホームへ戻る', 'Back to home', 'Volver al inicio'),
            note: t('ホームへ。', 'Go back to home.', 'Vuelve al inicio.'),
        },
    ];


    useEffect(() => {
        if (orderedCategories.length === 0) {
            if (activeCategory !== '') setActiveCategory('');
            return;
        }
        if (!orderedCategories.some((cat) => cat.category === activeCategory)) {
            setActiveCategory(orderedCategories[0].category);
        }
    }, [orderedCategories, activeCategory]);

    if (loading) {
        return (
            <div className="info-page">
                <p>{t('読み込み中...', 'Loading...', 'Cargando...')}</p>
            </div>
        );
    }

    const effectiveCategory = orderedCategories.find((cat) => cat.category === activeCategory) ?? orderedCategories[0] ?? null;

    return (
        <div className="info-page">
            <nav className="awards-breadcrumb">
                <Link href={withLocalePrefix(locale, '/')}>{t('ホーム', 'Home', 'Inicio')}</Link>
                {' › '}
                <span>{t('メジャーリーグ（MLB）アワード', 'Major League Baseball (MLB) Awards', 'Premios de Grandes Ligas (MLB)')}</span>
            </nav>
            <h1>{t('メジャーリーグ（MLB）アワード', 'Major League Baseball (MLB) Awards', 'Premios de Grandes Ligas (MLB)')}</h1>

            {orderedCategories.length === 0 ? (
                <PageEmptyState
                    id="mlb-awards-empty-state"
                    title={t('表示できるアワードがありません。', 'No award categories are available.', 'No hay categorías de premios disponibles.')}
                    paragraphs={[
                        t(
                            '表示できるアワードカテゴリがありません。',
                            'No award categories are available.',
                            'No hay categorías de premios disponibles.',
                        ),
                        t(
                            'MLBトップやNPBアワード一覧から探してください。',
                            'Use the MLB home page or the NPB awards list.',
                            'Usa la página principal de MLB o la lista de premios de NPB.',
                        ),
                    ]}
                    links={emptyStateLinks}
                    linksLabel={t('関連リンク', 'Related links', 'Enlaces relacionados')}
                />
            ) : (
                <>
                    {orderedCategories.length > 1 && (
                        <div
                            className="info-tabs"
                            role="tablist"
                            aria-label={t('メジャーリーグ（MLB）アワードカテゴリ', 'Major League Baseball (MLB) award categories', 'Categorías de premios de Grandes Ligas (MLB)')}
                        >
                            {orderedCategories.map((cat) => (
                                <button
                                    key={cat.category}
                                    type="button"
                                    role="tab"
                                    aria-selected={effectiveCategory?.category === cat.category}
                                    className={`info-tab-button ${effectiveCategory?.category === cat.category ? 'active' : ''}`}
                                    onClick={() => setActiveCategory(cat.category)}
                                >
                                    {categoryName(cat.category)}
                                </button>
                            ))}
                        </div>
                    )}

                    {effectiveCategory && (
                        <section className="awards-category-section">
                            {orderedCategories.length <= 1 && (
                                <h2 className="awards-category-title">{categoryName(effectiveCategory.category)}</h2>
                            )}
                            <ul className="awards-list">
                                {effectiveCategory.awards.map((award) => (
                                    <li key={award.awardId} className="awards-list-item">
                                        <Link href={withLocalePrefix(locale, `/awards/${awardSlug(award.awardId)}`)}>
                                            {awardName(award)}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </>
            )}
            <ScrollToTopButton />
        </div>
    );
}
